export const PENDO_ROLE_CUSTOM_STAFF = 'custom_staff_role';
export const PENDO_ROLE_CLIENT = 'client';
export const PENDO_TEACHER_PERSONA = 'teacher';
export const PENDO_STUDENT_PERSONA = 'student_household_side';

const KNOWN_STAFF_ROLES = new Set([
    'superuser',
    'owner',
    'manager',
    'staff',
    'limited_staff',
]);

type PendoUser = {
    id?: string | null;
    email?: string | null;
    name?: string | null;
    staffRole?: string | null;
    dependents?: Array<{ id?: string | null }> | null;
    accountManagers?: Array<{ id?: string | null }> | null;
};

type PendoBusiness = {
    name?: string | null;
    fullName?: string | null;
    status?: string | null;
    notificationsEnabled?: boolean | null;
    smsFeatureEnabled?: boolean | null;
    federatedLoginEnabled?: boolean | null;
    marketingPlusEnabled?: boolean | null;
    campaignFeatureEnabled?: boolean | null;
    ongoingCampaignsFeatureEnabled?: boolean | null;
    crmEnabled?: boolean | null;
    advancedPayrollSettingsEnabled?: boolean | null;
    voipEnabled?: boolean | null;
    advancedReportingEnabled?: boolean | null;
    webhooksEnabled?: boolean | null;
    lessonMateFeatureEnabled?: boolean | null;
    videoRecordingRequestEnabled?: boolean | null;
    mailchimpFeatureEnabled?: boolean | null;
};

export const getNormalizedRoleTier = (staffRole?: string | null): string => {
    if (!staffRole) {
        return PENDO_ROLE_CLIENT;
    }

    if (KNOWN_STAFF_ROLES.has(staffRole)) {
        return staffRole;
    }

    return PENDO_ROLE_CUSTOM_STAFF;
};

export const buildPendoVisitorMetadata = (user: PendoUser) => {
    const staffRole = user?.staffRole || null;
    const dependentsCount = user?.dependents?.length || 0;
    const accountManagersCount = user?.accountManagers?.length || 0;
    const nameFromUser = user?.name?.trim() || '';
    const [firstName = null, ...lastNameParts] = nameFromUser
        ? nameFromUser.split(/\s+/)
        : [];
    const lastName = lastNameParts.length ? lastNameParts.join(' ') : null;

    // V1 product mapping:
    // - staffRole presence => teacher-side persona
    // - no staffRole => student/household-side persona
    // Relationship-specific instructional personas will be layered later.
    const isTeacherSide = Boolean(staffRole);

    return {
        id: user?.id,
        email: user?.email,
        firstName,
        lastName,

        role: staffRole,
        rawStaffRole: staffRole,
        normalizedRoleTier: getNormalizedRoleTier(staffRole),

        teacherStudentPersona: isTeacherSide
            ? PENDO_TEACHER_PERSONA
            : PENDO_STUDENT_PERSONA,
        isTeacherSide,
        isStudentSide: !isTeacherSide,

        dependentsCount,
        accountManagersCount,
        isAccountManager: dependentsCount > 0,
        isDependent: accountManagersCount > 0,
    };
};

export const buildPendoAccountMetadata = (business: PendoBusiness) => ({
    id: business?.name,
    accountName: business?.fullName,
    payingStatus: business?.status,

    notificationsEnabled: business?.notificationsEnabled,

    smsFeatureEnabled: business?.smsFeatureEnabled,
    federatedLoginEnabled: business?.federatedLoginEnabled,

    plusReputationManagement: business?.marketingPlusEnabled,
    plusEmailSmsCampaigns: business?.campaignFeatureEnabled,
    plusOngoingCampaigns: business?.ongoingCampaignsFeatureEnabled,
    plusProspectsManagement: business?.crmEnabled,

    advancedPayrollSettingsEnabled: business?.advancedPayrollSettingsEnabled,
    voipEnabled: business?.voipEnabled,
    advancedReportingEnabled: business?.advancedReportingEnabled,
    webhooksEnabled: business?.webhooksEnabled,

    lessonMateFeatureEnabled: business?.lessonMateFeatureEnabled,
    videoRecordingRequestEnabled: business?.videoRecordingRequestEnabled,
    mailchimpFeatureEnabled: business?.mailchimpFeatureEnabled,
});

export const buildPendoInitSignature = (
    user: PendoUser,
    business: PendoBusiness,
) =>
    JSON.stringify({
        key: `${user?.id || ''}:${business?.name || ''}`,
        visitor: buildPendoVisitorMetadata(user),
        account: buildPendoAccountMetadata(business),
    });
